/**
 * ReportDetailDialog Component - Phase 5 Implementation
 * Modal dialog component for displaying detailed report views with chart/table tabs
 * Includes date range selector and export functionality
 */

import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { 
  RefreshCw, 
  BarChart3, 
  Table as TableIcon,
  TrendingUp,
  TrendingDown
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { useReportsStore } from '../../stores/reportsStore';
import { ReportType, ReportTabType } from '../../types/reports';
import DateRangeSelector from './DateRangeSelector';
import ExportDropdown from './ExportDropdown';
import { ReportChart } from './charts';
import ReportTable from './ReportTable';

interface ReportDetailDialogProps {
  reportType: ReportType | null;
  isOpen: boolean;
  onClose: () => void;
}

const ReportDetailDialog: React.FC<ReportDetailDialogProps> = ({
  reportType,
  isOpen,
  onClose,
}) => {
  const {
    reports,
    reportMetadata,
    activeTab,
    globalDateRange,
    isLoading,
    error,
    setActiveTab,
    setGlobalDateRange,
    refreshReport,
    exportReport,
  } = useReportsStore();

  const reportData = reportType ? reports[reportType] : null;
  const metadata = reportType ? reportMetadata[reportType] : null;

  const handleRefresh = () => {
    if (reportType) {
      refreshReport(reportType);
    }
  };

  const handleDateRangeChange = (range: any) => {
    setGlobalDateRange(range);
    if (reportType) {
      refreshReport(reportType);
    }
  };

  const handleExport = (config: any) => {
    if (reportType) {
      exportReport(reportType, config);
    }
  };

  if (!metadata || !reportType) {
    return null;
  }

  const IconComponent = metadata.icon;
  const hasError = error || reportData?.status === 'error';
  const isEmpty = reportData?.status === 'empty';
  const isLoadingData = isLoading || reportData?.status === 'loading';

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl h-[90vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className={cn("p-2 rounded-lg text-white", metadata.color)}>
                <IconComponent className="h-5 w-5" />
              </div>
              <div>
                <DialogTitle className="text-lg font-semibold">
                  {metadata.title}
                </DialogTitle>
                <DialogDescription className="text-sm">
                  {metadata.description}
                </DialogDescription>
              </div>
              <Badge variant="outline" className="ml-2">
                {metadata.category}
              </Badge>
            </div>
            
            <div className="flex items-center space-x-2">
              <DateRangeSelector
                value={globalDateRange}
                onChange={handleDateRangeChange}
                disabled={isLoadingData}
                className="w-64"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefresh}
                disabled={isLoadingData}
                className="px-3"
              >
                <RefreshCw className={cn("h-4 w-4", isLoadingData && "animate-spin")} />
              </Button>
              <ExportDropdown
                reportType={reportType}
                data={reportData?.chartData || []}
                onExport={handleExport}
                disabled={Boolean(isLoadingData || hasError || isEmpty)}
              />
            </div>
          </div>
        </DialogHeader>

        {/* Report Summary Cards */}
        {reportData?.summary && !isLoadingData && (
          <div className="grid grid-cols-4 gap-4 mb-4 flex-shrink-0">
            {Object.entries(reportData.summary.keyMetrics).map(([key, value]) => (
              <Card key={key} className="p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground capitalize">
                      {key.replace(/([A-Z])/g, ' $1').trim()}
                    </p>
                    <p className="text-lg font-semibold">
                      {typeof value === 'number' 
                        ? value.toLocaleString()
                        : value
                      }
                    </p>
                  </div>
                  {reportData.summary.trends[key] && (
                    <div className={cn(
                      "flex items-center space-x-1 text-xs",
                      reportData.summary.trends[key] > 0 
                        ? "text-green-600" 
                        : "text-red-600"
                    )}>
                      {reportData.summary.trends[key] > 0 ? (
                        <TrendingUp className="h-3 w-3" />
                      ) : (
                        <TrendingDown className="h-3 w-3" />
                      )}
                      <span>{Math.abs(reportData.summary.trends[key])}%</span>
                    </div>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Main Content Tabs */}
        <div className="flex-1 overflow-auto">
          <Tabs 
            value={activeTab} 
            onValueChange={(value) => setActiveTab(value as ReportTabType)}
            className="h-full flex flex-col"
          >
            <TabsList className="grid w-full grid-cols-2 mb-4 flex-shrink-0">
              <TabsTrigger value="chart" className="flex items-center space-x-2">
                <BarChart3 className="h-4 w-4" />
                <span>Chart View</span>
              </TabsTrigger>
              <TabsTrigger value="table" className="flex items-center space-x-2">
                <TableIcon className="h-4 w-4" />
                <span>Table View</span>
              </TabsTrigger>
            </TabsList>

            <div className="flex-1 overflow-auto">
              <TabsContent value="chart" className="h-full m-0">
                <Card className="h-full flex flex-col">
                  <CardHeader className="flex-shrink-0">
                    <CardTitle className="text-base">Chart Visualization</CardTitle>
                  </CardHeader>
                  <CardContent className="flex-1 overflow-auto p-4">
                    {hasError ? (
                      <div className="flex items-center justify-center h-full">
                        <div className="text-center space-y-2">
                          <p className="text-destructive font-medium">Failed to load chart data</p>
                          <p className="text-sm text-muted-foreground">
                            {error || reportData?.error || 'Unknown error occurred'}
                          </p>
                          <Button variant="outline" size="sm" onClick={handleRefresh}>
                            <RefreshCw className="h-4 w-4 mr-2" />
                            Retry
                          </Button>
                        </div>
                      </div>
                    ) : isEmpty ? (
                      <div className="flex items-center justify-center h-full">
                        <div className="text-center space-y-2">
                          <BarChart3 className="h-12 w-12 text-muted-foreground mx-auto" />
                          <p className="text-muted-foreground">No data available for the selected date range</p>
                          <p className="text-sm text-muted-foreground">
                            Try adjusting the date range or check back later
                          </p>
                        </div>
                      </div>
                    ) : isLoadingData ? (
                      <div className="h-full space-y-4">
                        <Skeleton className="h-8 w-48" />
                        <Skeleton className="h-64 w-full" />
                        <div className="flex space-x-4">
                          <Skeleton className="h-4 w-20" />
                          <Skeleton className="h-4 w-20" />
                          <Skeleton className="h-4 w-20" />
                        </div>
                      </div>
                    ) : (
                      <ReportChart
                        reportType={reportType}
                        data={reportData?.chartData || []}
                        className="h-full"
                      />
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="table" className="h-full m-0">
                <Card className="h-full flex flex-col">
                  <CardHeader className="flex-shrink-0">
                    <CardTitle className="text-base">Data Table</CardTitle>
                  </CardHeader>
                  <CardContent className="flex-1 overflow-hidden p-4">
                    {hasError ? (
                      <div className="flex items-center justify-center h-full">
                        <div className="text-center space-y-2">
                          <p className="text-destructive font-medium">Failed to load table data</p>
                          <p className="text-sm text-muted-foreground">
                            {error || reportData?.error || 'Unknown error occurred'}
                          </p>
                          <Button variant="outline" size="sm" onClick={handleRefresh}>
                            <RefreshCw className="h-4 w-4 mr-2" />
                            Retry
                          </Button>
                        </div>
                      </div>
                    ) : isEmpty ? (
                      <div className="flex items-center justify-center h-full">
                        <div className="text-center space-y-2">
                          <TableIcon className="h-12 w-12 text-muted-foreground mx-auto" />
                          <p className="text-muted-foreground">No data available for the selected date range</p>
                          <p className="text-sm text-muted-foreground">
                            Try adjusting the date range or check back later
                          </p>
                        </div>
                      </div>
                    ) : isLoadingData ? (
                      <div className="h-full space-y-4">
                        <div className="flex space-x-4 mb-4">
                          <Skeleton className="h-8 w-32" />
                          <Skeleton className="h-8 w-24" />
                          <Skeleton className="h-8 w-28" />
                        </div>
                        <div className="space-y-2">
                          {Array.from({ length: 10 }).map((_, i) => (
                            <Skeleton key={i} className="h-12 w-full" />
                          ))}
                        </div>
                      </div>
                    ) : (
                      <ReportTable
                        reportType={reportType}
                        data={reportData?.tableData || []}
                        className="h-full"
                      />
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </div>
          </Tabs>
        </div>

        {/* Footer with metadata */}
        {reportData && !isLoadingData && (
          <div className="flex-shrink-0 pt-4 border-t text-xs text-muted-foreground flex justify-between items-center">
            <span>
              Last updated: {reportData.lastUpdated.toLocaleString()}
            </span>
            <span>
              {reportData.summary.totalRecords.toLocaleString()} records found
            </span>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ReportDetailDialog;